import { reduce, mapValues, some, orderBy } from "lodash-es";
import uuid from "shortid";
import sequenceSelector from "./sequenceSelector";
import orfsSelector from "./orfsSelector";
import { createSelector } from "reselect";
import { getAminoAcidDataForEachBaseOfDna } from "@teselagen/sequence-utils";
import translationsRawSelector from "./translationsRawSelector";
import translationSearchMatchesSelector from "./translationSearchMatchesSelector";
import {
  expandOrContractRangeByLength,
  normalizePositionByRangeLength,
  isRangeWithinRange,
  getRangeLength
} from "@teselagen/range-utils";
import cdsFeaturesSelector from "./cdsFeaturesSelector";
import circularSelector from "./circularSelector";
import proteinDomainFeaturesSelector from "./proteinDomainFeaturesSelector";
function translationSpansInSameFrame(t, other, seqLen) {
  if (t.forward !== other.forward) return false;
  const cs1 = t?.notes?.codon_start?.[0] - 1 || 0;
  const cs2 = other?.notes?.codon_start?.[0] - 1 || 0;
  const s1 = t.forward ? t.start - cs1 : t.end + cs1;
  const s2 = other.forward ? other.start - cs2 : other.end + cs2;
  if (((s1 % 3 + 3) % 3 + seqLen) % 3 !== ((s2 % 3 + 3) % 3 + seqLen) % 3)
    return false;
  return isRangeWithinRange(t, other, seqLen);
}
function translationsSelector(isCircular, translationSearchMatches = [], sequence = "", orfs = [], showOrfTranslations, showOrfs, cdsFeatures = [], showCdsFeatureTranslations, showFeatures, translations = {}, frameTranslations = {}, proteinDomainFeatures = [], isProtein, proteinSequence, selectionLayer = { start: -1, end: -1 }) {
  const allTranslations = [];
  translationSearchMatches.forEach((match) => {
    if (!match) return;
    const id = match.id || uuid();
    allTranslations.push({
      ...match,
      id,
      priority: 1,
      translationType: "AA Search Match",
      isOrf: true,
      //pass isOrf = true here in order to not have it show up in the properties window
      forward: !match.bottomStrand
    });
  });
  reduce(
    translations,
    (acc, translation) => {
      if (!translation.isOrf) {
        allTranslations.push({
          ...translation,
          priority: 2,
          translationType: "User Created"
        });
      }
    },
    {}
  );
  if (showCdsFeatureTranslations && showFeatures) {
    cdsFeatures.forEach((cdsFeature) => {
      allTranslations.push({
        ...cdsFeature,
        priority: 3,
        translationType: "CDS Feature"
      });
    });
  }
  if (showOrfTranslations && showOrfs) {
    orfs.forEach((orf) => {
      allTranslations.push({ ...orf, priority: 4, translationType: "ORF" });
    });
  }
  if (showCdsFeatureTranslations && showFeatures) {
    proteinDomainFeatures.forEach((proteinDomain) => {
      allTranslations.push({
        ...proteinDomain,
        priority: 5,
        translationType: "Protein Domain Feature"
      });
    });
  }
  reduce(
    frameTranslations,
    (acc, isActive, frameName) => {
      const frameOffset = Number(frameName);
      if (isActive) {
        const id = uuid();
        allTranslations.push({
          id,
          priority: 6,
          start: isCircular || frameOffset > 0 ? normalizePositionByRangeLength(
            0 + frameOffset + (frameOffset > 0 ? -1 : 1),
            sequence.length
          ) : 0,
          end: isCircular || frameOffset < 0 ? normalizePositionByRangeLength(
            sequence.length - 1 + frameOffset + (frameOffset > 0 ? -1 : 1),
            sequence.length
          ) : sequence.length - 1,
          translationType: "Frame",
          forward: frameOffset > 0,
          isOrf: true
          //pass isOrf = true here in order to not have it show up in the properties window
        });
      }
    },
    {}
  );
  const seqLen = sequence.length;
  if (!isProtein && seqLen > 0 && selectionLayer && selectionLayer.start > -1 && selectionLayer.end > -1) {
    const selCandidate = {
      id: "selectionTranslation",
      start: selectionLayer.start,
      end: selectionLayer.end,
      forward: selectionLayer.forward !== false,
      priority: 2.5,
      translationType: "Selection",
      isOrf: true
      // Do not show as a permanent annotation in properties window
    };
    const isAlreadyCovered = some(
      allTranslations,
      (other) => translationSpansInSameFrame(selCandidate, other, seqLen)
    );
    if (!isAlreadyCovered) {
      allTranslations.push(selCandidate);
    }
  }
  const sortedTranslations = orderBy(
    allTranslations,
    [
      (t) => t.priority <= 2.5 ? t.priority : 3,
      // Search, User, Selection first
      (t) => getRangeLength(t, seqLen),
      // Then by length (longer wins)
      "priority"
      // Then by priority (CDS > ORF > PD)
    ],
    ["asc", "desc", "asc"]
  );
  const translationsToPass = {};
  const acceptedTranslations = [];
  sortedTranslations.forEach((t) => {
    const isSuperseded = some(acceptedTranslations, (other) => {
      if (t.priority <= 2.5) return false;
      if (other.priority <= 2.5) return false;
      return translationSpansInSameFrame(t, other, seqLen);
    });
    if (!isSuperseded) {
      translationsToPass[t.id] = t;
      acceptedTranslations.push(t);
    }
  });
  return mapValues(translationsToPass, (translation) => {
    const codonStart = translation?.notes?.codon_start?.[0] - 1 || 0;
    const expandedRange = expandOrContractRangeByLength(
      translation,
      -codonStart,
      true,
      sequence.length
    );
    expandedRange.aminoAcids = getAminoAcidDataForEachBaseOfDna(
      sequence,
      expandedRange.forward,
      expandedRange
    );
    return expandedRange;
  });
}
export default createSelector(
  circularSelector,
  translationSearchMatchesSelector,
  sequenceSelector,
  orfsSelector,
  (state) => state.annotationVisibility?.orfTranslations,
  (state) => state.annotationVisibility?.orfs,
  cdsFeaturesSelector,
  (state) => state.annotationVisibility?.cdsFeatureTranslations,
  (state) => state.annotationVisibility?.features,
  translationsRawSelector,
  (state) => state.frameTranslations,
  proteinDomainFeaturesSelector,
  (state) => state.sequenceData?.isProtein,
  (state) => state.sequenceData?.proteinSequence,
  (state) => state.selectionLayer,
  translationsSelector
);
